/**
 * App-wide error boundary. Without one, any uncaught render error unmounts the
 * whole React tree → a blank page with no recovery but a manual hard refresh.
 *
 * The most common cause here is a STALE cached bundle after a deploy (old code
 * meeting new data, or a chunk/asset hash that no longer exists). So on a crash
 * we auto-recover ONCE per session: unregister the service worker, clear the
 * caches, and reload to fetch the fresh build. A loop guard (20s) means a genuine
 * bug shows a "Reload" fallback after that single attempt instead of looping.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

const RELOAD_KEY = 'daaily.crashReloadAt';
const GUARD_MS = 20_000;

/** Best-effort: drop the service worker + caches, then reload from network. */
async function hardReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore — reload anyway */
  }
  window.location.reload();
}

interface State {
  crashed: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    // Auto-recover once per session (guards against a reload loop on a real bug).
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    } catch {
      /* ignore */
    }
    const now = Date.now();
    if (now - last > GUARD_MS) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(now));
      } catch {
        /* ignore */
      }
      void hardReload();
    }
  }

  render(): ReactNode {
    if (this.state.crashed) {
      // Inline styles so the fallback renders even if the stylesheet is stale/missing.
      // Colours read the design tokens (styles/tokens.css) with dark-theme fallbacks.
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            background: 'rgb(var(--bg, 22 21 20))',
            color: 'rgb(var(--fg, 237 237 236))',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '15px' }}>Something went wrong.</div>
          <div style={{ fontSize: '13px', color: 'rgb(var(--fg-muted, 168 166 161))' }}>
            This is usually an out-of-date version — reloading fixes it.
          </div>
          <button
            type="button"
            onClick={() => void hardReload()}
            style={{
              background: 'rgb(var(--primary, 237 237 236))',
              color: 'rgb(var(--primary-fg, 22 21 20))',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 18px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
